import React from 'react'
import {
  Form, Row, Col, Button, Card, Collapse,
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  IBroadcastFormMap,
  IEnterpriseBroadcastFormGeneratorMap,
  IBroadcastFormReportGroup,
  IBroadcastFormRecipientResponseReportRow,
} from 'types/BroadcastForm'
import MultiSelectInput from 'components/Inputs/MultiSelectInput'
import { IOptions } from 'components/Inputs/Select'

export interface IEnterpriseSurveyReportFilterState {
  formTypes: string[]
  generatorIds: number[]
  accountIds: number[]
  groupIds: number[]
  excludedGroupIds: number[]
  userTypes: string[]
  serviceLevels: string[]
  jobClassifications: string[]
  jobRoles: string[]
  employeeStatuses: string[]
  employeeTypes: string[]
  terminationReasons: string[]
  questionIds: number[]
  hasResponse: boolean | null // null = all, true = only with response, false = only without
  recipientNameSearch: string
}

interface EnterpriseSurveyReportFilterProps {
  filter: IEnterpriseSurveyReportFilterState
  onChange: (next: IEnterpriseSurveyReportFilterState) => void
  forms: IBroadcastFormMap
  generators: IEnterpriseBroadcastFormGeneratorMap
  groups: IBroadcastFormReportGroup[]
  allRecipients?: IBroadcastFormRecipientResponseReportRow[]
  filteredCount?: number
}

export const EnterpriseSurveyReportFilter: React.FC<EnterpriseSurveyReportFilterProps> = ({
  filter,
  onChange,
  forms,
  generators,
  groups,
  allRecipients,
  filteredCount,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false)

  const allForms = React.useMemo(
    () => Object.values(forms ?? {}),
    [forms],
  )

  const allGenerators = React.useMemo(
    () => Object.values(generators ?? {}),
    [generators],
  )

  const allAccounts = React.useMemo(
    () => {
      if (!allRecipients) return []
      const map = new Map<number, string>()
      allRecipients.forEach((r) => {
        if (r.AccountId && !map.has(r.AccountId)) {
          const accountName = r.Account_Name && r.Account_Name.trim() !== ''
            ? r.Account_Name
            : `Account ${r.AccountId}`
          map.set(r.AccountId, accountName)
        }
      })
      return Array.from(map.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    [allRecipients],
  )

  const allFormTypes = React.useMemo(
    () => Array.from(new Set(allForms.map((f) => f.FormType))).sort(),
    [allForms],
  )

  // could also derive this dynamically from data if needed
  const allUserTypes = React.useMemo(
    () => ['resident', 'staff', 'family'],
    [],
  )

  const formTypeOptions = React.useMemo<IOptions[]>(
    () => allFormTypes.map((ft) => ({ label: ft, value: ft })),
    [allFormTypes],
  )

  const generatorOptions = React.useMemo<IOptions[]>(
    () => allGenerators.map((g) => ({ label: g.SurveyName, value: g.GeneratorId.toString() })),
    [allGenerators],
  )

  const accountOptions = React.useMemo<IOptions[]>(
    () => allAccounts.map((a) => ({ label: a.name, value: a.id.toString() })),
    [allAccounts],
  )

  const groupOptions = React.useMemo<IOptions[]>(
    () => groups.map((g) => ({
      label: `${g.AccountName} - ${g.GroupName}`,
      value: g.GroupId.toString(),
    })),
    [groups],
  )

  const userTypeOptions = React.useMemo<IOptions[]>(
    () => allUserTypes.map((ut) => ({ label: ut, value: ut })),
    [allUserTypes],
  )

  // Extract unique service levels from raw report data
  const allServiceLevels = React.useMemo(
    () => {
      if (!allRecipients) return []
      const serviceLevels = new Set<string>()
      allRecipients.forEach((r) => {
        if (r.ServiceLevel && r.ServiceLevel.trim() !== '') {
          serviceLevels.add(r.ServiceLevel)
        }
      })
      return Array.from(serviceLevels).sort()
    },
    [allRecipients],
  )

  const serviceLevelOptions = React.useMemo<IOptions[]>(
    () => allServiceLevels.map((sl) => ({ label: sl, value: sl })),
    [allServiceLevels],
  )

  // Extract unique job classifications from raw report data
  const allJobClassifications = React.useMemo(
    () => {
      if (!allRecipients) return []
      const classifications = new Set<string>()
      allRecipients.forEach((r) => {
        if (r.JobClassification && r.JobClassification.trim() !== '') {
          classifications.add(r.JobClassification)
        }
      })
      return Array.from(classifications).sort()
    },
    [allRecipients],
  )

  const jobClassificationOptions = React.useMemo<IOptions[]>(
    () => allJobClassifications.map((jc) => ({ label: jc, value: jc })),
    [allJobClassifications],
  )

  // Extract unique job roles from raw report data
  const allJobRoles = React.useMemo(
    () => {
      if (!allRecipients) return []
      const roles = new Set<string>()
      allRecipients.forEach((r) => {
        if (r.JobRole && r.JobRole.trim() !== '') {
          roles.add(r.JobRole)
        }
      })
      return Array.from(roles).sort()
    },
    [allRecipients],
  )

  const jobRoleOptions = React.useMemo<IOptions[]>(
    () => allJobRoles.map((jr) => ({ label: jr, value: jr })),
    [allJobRoles],
  )

  // Extract unique employee statuses from raw report data
  const allEmployeeStatuses = React.useMemo(
    () => {
      if (!allRecipients) return []
      const statuses = new Set<string>()
      allRecipients.forEach((r) => {
        if (r.EmployeeStatus && r.EmployeeStatus.trim() !== '') {
          statuses.add(r.EmployeeStatus)
        }
      })
      return Array.from(statuses).sort()
    },
    [allRecipients],
  )

  const employeeStatusOptions = React.useMemo<IOptions[]>(
    () => allEmployeeStatuses.map((es) => ({ label: es, value: es })),
    [allEmployeeStatuses],
  )

  // Extract unique employee types from raw report data
  const allEmployeeTypes = React.useMemo(
    () => {
      if (!allRecipients) return []
      const types = new Set<string>()
      allRecipients.forEach((r) => {
        if (r.EmployeeType && r.EmployeeType.trim() !== '') {
          types.add(r.EmployeeType)
        }
      })
      return Array.from(types).sort()
    },
    [allRecipients],
  )

  const employeeTypeOptions = React.useMemo<IOptions[]>(
    () => allEmployeeTypes.map((et) => ({ label: et, value: et })),
    [allEmployeeTypes],
  )

  // Extract unique termination reasons from raw report data
  const allTerminationReasons = React.useMemo(
    () => {
      if (!allRecipients) return []
      const reasons = new Set<string>()
      allRecipients.forEach((r) => {
        if (r.TerminationReason && r.TerminationReason.trim() !== '') {
          reasons.add(r.TerminationReason)
        }
      })
      return Array.from(reasons).sort()
    },
    [allRecipients],
  )

  const terminationReasonOptions = React.useMemo<IOptions[]>(
    () => allTerminationReasons.map((tr) => ({ label: tr, value: tr })),
    [allTerminationReasons],
  )

  // Extract unique questions from raw report data
  const allQuestions = React.useMemo(
    () => {
      if (!allRecipients) return []
      const questionMap = new Map<number, string>()
      allRecipients.forEach((r) => {
        if (r.QuestionId != null && r.QuestionText && r.QuestionText.trim() !== '') {
          if (!questionMap.has(r.QuestionId)) {
            questionMap.set(r.QuestionId, r.QuestionText)
          }
        }
      })
      return Array.from(questionMap.entries())
        .map(([id, text]) => ({ id, text }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    [allRecipients],
  )

  const questionOptions = React.useMemo<IOptions[]>(
    () => allQuestions.map((q) => ({
      label: q.text.length > 80 ? `${q.text.substring(0, 80)}...` : q.text,
      value: q.id.toString(),
    })),
    [allQuestions],
  )

  const handleChange = (patch: Partial<IEnterpriseSurveyReportFilterState>) => {
    onChange({
      ...filter,
      ...patch,
    })
  }

  const handleReset = () => {
    onChange({
      formTypes: [],
      generatorIds: [],
      accountIds: [],
      groupIds: [],
      excludedGroupIds: [],
      userTypes: [],
      serviceLevels: [],
      jobClassifications: [],
      jobRoles: [],
      employeeStatuses: [],
      employeeTypes: [],
      terminationReasons: [],
      questionIds: [],
      hasResponse: null,
      recipientNameSearch: '',
    })
  }

  const hasActiveFilters = React.useMemo(
    () => (
      filter.formTypes.length > 0
      || filter.generatorIds.length > 0
      || filter.accountIds.length > 0
      || filter.groupIds.length > 0
      || filter.excludedGroupIds.length > 0
      || filter.userTypes.length > 0
      || filter.serviceLevels.length > 0
      || filter.jobClassifications.length > 0
      || filter.jobRoles.length > 0
      || filter.employeeStatuses.length > 0
      || filter.employeeTypes.length > 0
      || filter.terminationReasons.length > 0
      || filter.questionIds.length > 0
      || filter.hasResponse !== null
      || (filter.recipientNameSearch && filter.recipientNameSearch.trim().length > 0)
    ),
    [filter],
  )

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header className="bg-white border-bottom">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              className="p-0 mr-2 text-decoration-none"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
            >
              <FontAwesomeIcon
                icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                className="text-muted"
              />
            </Button>
            <h5 className="mb-0">Filters</h5>
            {hasActiveFilters && filteredCount != null && allRecipients && (
              <span className="ml-3 text-muted small">
                {filteredCount.toLocaleString()}
                /
                {allRecipients.length.toLocaleString()}
                {' '}
                question responses after filtering
              </span>
            )}
          </div>
          {hasActiveFilters && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleReset}
            >
              Reset All
            </Button>
          )}
        </div>
      </Card.Header>
      <Collapse in={isExpanded}>
        <div>
          <Card.Body>
            <Form>
              {/* Survey Filtering Section */}
              <div className="mb-4">
                <h6 className="text-muted mb-3 font-weight-bold">Survey Filtering</h6>
                <Row>
                  <Col md={4} lg={3}>
                    <MultiSelectInput
                      id="filterFormType"
                      name="filterFormType"
                      label="Form Type"
                      showResetButton
                      options={formTypeOptions}
                      value={filter.formTypes}
                      onChange={(values: string[]) => handleChange({
                        formTypes: values,
                      })}
                      placeholder="Select form types..."
                    />
                  </Col>

                  <Col md={4} lg={3}>
                    <MultiSelectInput
                      id="filterGenerator"
                      name="filterGenerator"
                      label="Generator"
                      showResetButton
                      options={generatorOptions}
                      value={filter.generatorIds.map((id) => id.toString())}
                      onChange={(values: string[]) => handleChange({
                        generatorIds: values.map((v) => Number(v)),
                      })}
                      placeholder="Select generators..."
                    />
                  </Col>

                  {accountOptions.length > 0 && (
                    <Col md={4} lg={3}>
                      <MultiSelectInput
                        id="filterAccount"
                        name="filterAccount"
                        label="Account"
                        showResetButton
                        options={accountOptions}
                        value={filter.accountIds.map((id) => id.toString())}
                        onChange={(values: string[]) => handleChange({
                          accountIds: values.map((v) => Number(v)),
                        })}
                        placeholder="Select accounts..."
                      />
                    </Col>
                  )}
                </Row>

                {groupOptions.length > 0 && (
                  <>
                    <Row className="mt-3">
                      <Col md={12}>
                        <MultiSelectInput
                          id="filterGroup"
                          name="filterGroup"
                          label="Groups"
                          showResetButton
                          options={groupOptions}
                          value={filter.groupIds.map((id) => id.toString())}
                          onChange={(values: string[]) => handleChange({
                            groupIds: values.map((v) => Number(v)),
                          })}
                          placeholder="Select groups..."
                        />
                      </Col>
                    </Row>
                    <Row className="mt-3">
                      <Col md={12}>
                        <MultiSelectInput
                          id="filterExcludedGroup"
                          name="filterExcludedGroup"
                          label="Group Exclusion Filter"
                          showResetButton
                          options={groupOptions}
                          value={filter.excludedGroupIds.map((id) => id.toString())}
                          onChange={(values: string[]) => handleChange({
                            excludedGroupIds: values.map((v) => Number(v)),
                          })}
                          placeholder="Select groups to exclude..."
                        />
                      </Col>
                    </Row>
                  </>
                )}

                {questionOptions.length > 0 && (
                  <Row className="mt-3">
                    <Col md={12}>
                      <MultiSelectInput
                        id="filterQuestion"
                        name="filterQuestion"
                        label="Questions"
                        showResetButton
                        options={questionOptions}
                        value={filter.questionIds.map((id) => id.toString())}
                        onChange={(values: string[]) => handleChange({
                          questionIds: values.map((v) => Number(v)),
                        })}
                        placeholder="Select questions..."
                      />
                    </Col>
                  </Row>
                )}
              </div>

              {/* Recipient Filtering Section */}
              <div className="mb-4">
                <h6 className="text-muted mb-3 font-weight-bold">Recipient Filtering</h6>
                <Row>
                  <Col md={4} lg={3}>
                    <Form.Group controlId="filterRecipientName" className="mt-3">
                      <Form.Label>Recipient Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by name..."
                        value={filter.recipientNameSearch || ''}
                        onChange={(e) => handleChange({
                          recipientNameSearch: e.target.value,
                        })}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4} lg={3}>
                    <MultiSelectInput
                      id="filterUserType"
                      name="filterUserType"
                      label="User Type"
                      showResetButton
                      options={userTypeOptions}
                      value={filter.userTypes}
                      onChange={(values: string[]) => handleChange({
                        userTypes: values,
                      })}
                      placeholder="Select user types..."
                    />
                  </Col>

                  <Col md={4} lg={3}>
                    <Form.Group controlId="filterHasResponse" className="mt-3">
                      <Form.Label>Response Status</Form.Label>
                      <Form.Control
                        as="select"
                        value={
                          // eslint-disable-next-line no-nested-ternary
                          filter.hasResponse === null
                            ? ''
                            : filter.hasResponse
                              ? 'with'
                              : 'without'
                        }
                        onChange={(e) => {
                          const val = e.target.value
                          if (!val) {
                            handleChange({ hasResponse: null })
                          } else if (val === 'with') {
                            handleChange({ hasResponse: true })
                          } else {
                            handleChange({ hasResponse: false })
                          }
                        }}
                      >
                        <option value="">All</option>
                        <option value="with">With responses</option>
                        <option value="without">Without responses</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  {serviceLevelOptions.length > 0 && (
                    <Col md={4} lg={3}>
                      <MultiSelectInput
                        id="filterServiceLevel"
                        name="filterServiceLevel"
                        label="Service Level"
                        showResetButton
                        options={serviceLevelOptions}
                        value={filter.serviceLevels}
                        onChange={(values: string[]) => handleChange({
                          serviceLevels: values,
                        })}
                        placeholder="Select service levels..."
                      />
                    </Col>
                  )}
                </Row>
              </div>

              {/* Employment Filtering Section */}
              {(jobClassificationOptions.length > 0
                || jobRoleOptions.length > 0
                || employeeStatusOptions.length > 0
                || employeeTypeOptions.length > 0
                || terminationReasonOptions.length > 0) && (
                <div className="mb-4">
                  <h6 className="text-muted mb-3 font-weight-bold">Employment Filtering</h6>
                  <Row>
                    {jobClassificationOptions.length > 0 && (
                      <Col md={4} lg={3}>
                        <MultiSelectInput
                          id="filterJobClassification"
                          name="filterJobClassification"
                          label="Job Classification"
                          showResetButton
                          options={jobClassificationOptions}
                          value={filter.jobClassifications}
                          onChange={(values: string[]) => handleChange({
                            jobClassifications: values,
                          })}
                          placeholder="Select job classifications..."
                        />
                      </Col>
                    )}

                    {jobRoleOptions.length > 0 && (
                      <Col md={4} lg={3}>
                        <MultiSelectInput
                          id="filterJobRole"
                          name="filterJobRole"
                          label="Job Role"
                          showResetButton
                          options={jobRoleOptions}
                          value={filter.jobRoles}
                          onChange={(values: string[]) => handleChange({
                            jobRoles: values,
                          })}
                          placeholder="Select job roles..."
                        />
                      </Col>
                    )}

                    {employeeStatusOptions.length > 0 && (
                      <Col md={4} lg={3}>
                        <MultiSelectInput
                          id="filterEmployeeStatus"
                          name="filterEmployeeStatus"
                          label="Employee Status"
                          showResetButton
                          options={employeeStatusOptions}
                          value={filter.employeeStatuses}
                          onChange={(values: string[]) => handleChange({
                            employeeStatuses: values,
                          })}
                          placeholder="Select employee statuses..."
                        />
                      </Col>
                    )}

                    {employeeTypeOptions.length > 0 && (
                      <Col md={4} lg={3}>
                        <MultiSelectInput
                          id="filterEmployeeType"
                          name="filterEmployeeType"
                          label="Employee Type"
                          showResetButton
                          options={employeeTypeOptions}
                          value={filter.employeeTypes}
                          onChange={(values: string[]) => handleChange({
                            employeeTypes: values,
                          })}
                          placeholder="Select employee types..."
                        />
                      </Col>
                    )}
                  </Row>

                  {terminationReasonOptions.length > 0 && (
                    <Row className="mt-3">
                      <Col md={4} lg={3}>
                        <MultiSelectInput
                          id="filterTerminationReason"
                          name="filterTerminationReason"
                          label="Termination Reason"
                          showResetButton
                          options={terminationReasonOptions}
                          value={filter.terminationReasons}
                          onChange={(values: string[]) => handleChange({
                            terminationReasons: values,
                          })}
                          placeholder="Select termination reasons..."
                        />
                      </Col>
                    </Row>
                  )}
                </div>
              )}
            </Form>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  )
}
